import { ReactElement, ReactNode, useCallback, useEffect, useState } from "react";
import { PencilIcon } from "@heroicons/react/outline";
import { NavLink } from "react-router-dom";

import BorderedPage from "components/BorderedPage";

import BorderLayout from "layouts/BorderLayout";

const CRAWLERS_TITLE = 'Crawlers';
const NEW_CRAWLER = 'New Crawler';

const EditableBreadcrumbPageTitle = ({ crawlerName, callback }: { crawlerName?: string, crawlerId?: number, callback: (newCrawlerName: string) => void}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedCrawlerName, setEditedCrawlerName] = useState(crawlerName);
  const [originalCrawlerName, setOriginalCrawlerName] = useState(crawlerName);

  const editingFinished = useCallback( () => {
    setIsEditing(false);
    if (editedCrawlerName) {
      setOriginalCrawlerName(editedCrawlerName);
      callback(editedCrawlerName);
    }

  }, [callback, editedCrawlerName]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      editingFinished();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditedCrawlerName(originalCrawlerName);
    }
  }, [editingFinished, originalCrawlerName, setIsEditing, setEditedCrawlerName]);

  useEffect(() => {
    if (isEditing) window.addEventListener('keydown', handleKeyDown);
    else window.removeEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEditing, handleKeyDown]);

  return (
    <div className="text-2xl flex flex-row flex-wrap gap-2 overflow-auto">
      <NavLink to="/crawlers">
        <div className="text-neutral-500 dark:text-neutral-500">{CRAWLERS_TITLE}</div>
      </NavLink>
      <div className="text-gray dark:text-neutral-600">/</div>
      <div className="shrink">
        {
          isEditing
          ? (<input autoFocus className="border border-slate-200" value={editedCrawlerName} onChange={(e) => setEditedCrawlerName(e.target.value)} onBlur={editingFinished}/>)
          : (<div className="flex flex-row items-center" onClick={() => setIsEditing(true)}>
            <div className="text-gray dark:text-neutral-600">{crawlerName || NEW_CRAWLER}</div>
            <div className="w-5 h-5 text-neutral-500 dark:text-neutral-500 ml-2.5"><PencilIcon/></div>
          </div>)
        }
      </div>
    </div>
  )
};

const BreadcrumbPageTitle = ({ crawlerName, buttons }: { crawlerName?: string, buttons?: ReactElement<any, any>}) => {
  return (
    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between w-full gap-4">
      <div className="text-2xl flex flex-row flex-wrap gap-2 overflow-auto">
        <NavLink to="/crawlers">
          <div className="text-neutral-500 dark:text-neutral-500">{CRAWLERS_TITLE}</div>
        </NavLink>
        <div className="text-neutral-500 dark:text-neutral-500">/</div>
        <div>{crawlerName || NEW_CRAWLER}</div>
      </div>
      <div className="w-full lg:w-auto">
        {buttons}
      </div>
    </div>
  )
};

// View mode
export function CrawlerPageLayout(props: {
  children: ReactNode;
  crawlerName?: string;
  crawlerId?: number;
  rightSidebar?: ReactElement<any, any> | ReactElement<any, any>[];
  actionButtons?: ReactElement<any, any> | ReactElement<any, any>[];
  readonly: true; // Read only, no callback
  titleButtons: ReactElement<any, any>; // Read only: reschedule,
  className?: string;
}): JSX.Element;

// Edit mode
export function CrawlerPageLayout(props: {
  children: ReactNode;
  crawlerName?: string;
  crawlerId?: number;
  rightSidebar?: ReactElement<any, any> | ReactElement<any, any>[];
  actionButtons?: ReactElement<any, any> | ReactElement<any, any>[];
  readonly?: false; // Not readonly, callback is required
  callback: (newCrawlerName: string) => void;
  className?: string;
}): JSX.Element;

export function CrawlerPageLayout({
  children,
  crawlerName,
  crawlerId,
  rightSidebar,
  actionButtons,
  readonly,
  callback,
  className,
  titleButtons,
}: {
  children: ReactNode;
  crawlerName?: string;
  crawlerId?: number;
  rightSidebar?: ReactElement<any, any> | ReactElement<any, any>[];
  actionButtons?: ReactElement<any, any> | ReactElement<any, any>[];
  readonly?: boolean;
  callback?: (newCrawlerName: string) => void;
  className?: string;
  titleButtons?: ReactElement<any, any>;
}): JSX.Element {

  const title = `${CRAWLERS_TITLE} / ${crawlerName || NEW_CRAWLER}`;

  return (
    <BorderLayout
      layout="horizontal"
      wrap="xl"
      bottom={ actionButtons && (
          <div className="py-5 space-y-5">
            <div className="w-full border-t border-slate-200"/>
            { actionButtons }
          </div>
        )
      }
      right={ rightSidebar && <div className="w-full xl:w-80 xl:h-full">{ rightSidebar }</div> }
    >
      <BorderedPage
        title={ readonly ?
          <BreadcrumbPageTitle crawlerName={ crawlerName } buttons={ titleButtons } /> :
          <EditableBreadcrumbPageTitle crawlerName={ crawlerName } crawlerId={ crawlerId } callback={ callback! } />
        }
        browserTitle={ title }
      >
        { children }
      </BorderedPage>
    </BorderLayout>
  );
}